import React, { useEffect, useRef, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import { getActiveBroadcasts } from "./broadcastService";
import "quill/dist/quill.snow.css";
import "./broadcast.css";

export default function BroadcastAnnouncements({ token }) {
  const [broadcasts, setBroadcasts] = useState([]);
  const dismissed = useRef(new Set());

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    let inFlight = false;
    let unauthorized = false;
    const controller = new AbortController();

    const refresh = async () => {
      if (!active || inFlight || unauthorized || document.visibilityState === "hidden") return;
      inFlight = true;
      try {
        const records = await getActiveBroadcasts(token, controller.signal);
        if (active) setBroadcasts(records);
      } catch (error) {
        if (active && (error.response?.status === 401 || error.response?.status === 403)) {
          unauthorized = true;
          setBroadcasts([]);
        }
      } finally {
        inFlight = false;
      }
    };

    refresh();
    const interval = window.setInterval(refresh, 60000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [token]);

  const pending = broadcasts.filter((broadcast) => !dismissed.current.has(broadcast.version));
  const close = () => {
    pending.forEach((broadcast) => dismissed.current.add(broadcast.version));
    setBroadcasts([]);
  };

  if (!pending.length) return null;
  const broadcast = pending[0];

  return (
    <Dialog open onClose={close} fullWidth maxWidth="md" PaperProps={{ sx: { maxWidth: 760 } }} scroll="paper" aria-labelledby="broadcast-announcements-title">
      <DialogTitle id="broadcast-announcements-title" sx={{ pr: 7, overflowWrap: "anywhere" }}>
        {broadcast.title}
        <IconButton aria-label="Close announcements" onClick={close} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
          <section className="broadcast-announcement" aria-label={broadcast.title}>
            <div className="ql-container ql-snow broadcast-rich-text">
              <div className="ql-editor" dangerouslySetInnerHTML={{ __html: broadcast.message }} />
            </div>
          </section>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} variant="contained" sx={{ backgroundColor: "#ff5b5b", "&:hover": { backgroundColor: "#df4545" } }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
